
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Tooltip,
    Stack,
    Divider,
} from "@mui/material";

import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

const getFileIcon = (fileName = "") => {
    const extension = fileName?.split(".").pop()?.toLowerCase();

    if (extension === "pdf") {
        return <PictureAsPdfOutlinedIcon color="error" />;
    }

    if (["jpg", "jpeg", "png"].includes(extension)) {
        return <ImageOutlinedIcon color="primary" />;
    }

    return <InsertDriveFileOutlinedIcon />;
};

const FileList = ({
    files = [],
    maxFiles = 5,
    onDownload,
    onDelete,
    deletingFileId = null,
}) => {
    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "background.paper",
            }}
        >
            <Box sx={{ px: 2, py: 1.5 }}>
                <Typography fontWeight={600}>
                    Uploaded Files ({files.length}/{maxFiles})
                </Typography>

                <Typography variant="body2" color="text.secondary">
                    PDF, JPG and PNG files
                </Typography>
            </Box>

            <Divider />

            {files.length === 0 ? (
                <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
                    <InsertDriveFileOutlinedIcon
                        sx={{
                            fontSize: 36,
                            color: "text.disabled",
                            mb: 1,
                        }}
                    />

                    <Typography variant="body2" color="text.secondary">
                        No files uploaded yet.
                    </Typography>
                </Box>
            ) : (
                files?.map((file, index) => (
                    <Box key={file.id ?? `${file.file_name}-${index}`}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                                px: 2,
                                py: 1.25,
                                "&:hover": {
                                    bgcolor: "action.hover",
                                },
                            }}
                        >
                            {getFileIcon(file.file_name)}

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    noWrap
                                    title={file.file_name}
                                >
                                    {file.file_name}
                                </Typography>
                            </Box>

                            <Tooltip title="Download file">
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label={`Download ${file.file_name}`}
                                        onClick={() => onDownload?.(file.file_name, file.id)}
                                        disabled={!onDownload}
                                    >
                                        <DownloadOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Delete file">
                                <span>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        aria-label={`Delete ${file.file_name}`}
                                        onClick={() => onDelete?.(file.file_name, file.id)}
                                        disabled={
                                            !onDelete ||
                                            deletingFileId === file.id
                                        }
                                    >
                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>

                        {index < files.length - 1 && <Divider className="divider" />}
                    </Box>
                ))
            )}
        </Paper>
    );
};

export default FileList;
